// 자동 화질 조절(설계서 §9.5.4) — **판정은 뷰어가 한다**.
//
// 🔴 왜 뷰어인가: 적응 로직을 에이전트에 넣으면 Windows·macOS·Android 세 곳에 각각 만들고 각각 검증해야
//    한다. 반면 뷰어에는 이미 1초 주기 수신 통계가 있다(rtc.ts startStats). 자동이 내린 결정은 **수동
//    변경과 똑같은 `q` 메시지**로 나가므로 에이전트는 자동인지 수동인지 알 필요가 없고, 정책이 한 곳에
//    모여 이렇게 **순수 함수로 단위 시험**할 수 있다.
//
// 이 파일에는 브라우저 API·타이머가 없다. 시각은 표본이 들고 온다(`tMs`) — 시험이 시간을 만들 수 있게.

import type { PresetId, QualitySpec } from './protocol';

/**
 * 자동 모드가 오르내리는 **사다리**. 임의 조합을 고르지 않는 이유: 예측 가능해야 하고(사용자가 "자동이
 * 지금 어디쯤" 을 알 수 있어야 한다), 조합 폭발 없이 시험할 수 있어야 한다.
 */
export const LADDER: readonly QualitySpec[] = [
  { preset: 'high', fps: 30 }, // L0 가장 좋음
  { preset: 'high', fps: 15 }, // L1
  { preset: 'med', fps: 15 }, // L2 ← 시작 지점(= 도입 전 기본값)
  { preset: 'med', fps: 10 }, // L3
  { preset: 'low', fps: 10 }, // L4
  { preset: 'low', fps: 5 }, // L5 가장 아낌
] as const;

/** 자동 시작 지점. 현행 기본값과 같은 칸이다. */
export const LADDER_START = 2;
const LADDER_TOP = 0;
const LADDER_BOTTOM = LADDER.length - 1;

// ── 판정 문턱 ──
/** 이 이상 잃으면 나쁜 표본. */
const LOSS_BAD = 0.03;
/** 이 미만이어야 좋은 표본. */
const LOSS_GOOD = 0.005;
/** 실효 fps 가 목표의 이 비율 미만이면 나쁜 표본. */
const FPS_BAD_RATIO = 0.6;
/** 실효 fps 가 목표의 이 비율 이상이어야 좋은 표본. */
const FPS_GOOD_RATIO = 0.9;

/** 최근 이 개수의 표본 중 */
const BAD_WINDOW = 3;
/** 이 개수 이상이 나쁘면 내린다(빠르게). */
const BAD_NEEDED = 2;
/** 좋은 표본이 연속 이만큼이면 올린다(느리게). */
const GOOD_NEEDED = 10;

// 🔴 내림 3초 / 올림 10초의 **비대칭**이 진동을 막는다. 대칭이면 경계에서 끝없이 오르내린다.

/** 올린 직후 이 시간 동안은 다시 판정하지 않는다(새 설정이 자리 잡을 틈). */
const UP_COOLDOWN_MS = 15_000;
/** 올린 뒤 이 시간 안에 내려가면 "올라가면 안 되는 칸" 으로 기억한다. */
const REGRET_WINDOW_MS = 30_000;
/** 기억한 천장을 이 시간 동안 시도하지 않는다. */
const CEILING_HOLD_MS = 60_000;

export interface AutoSample {
  /** 이 구간 손실률(0~1). */
  lossPct: number;
  /** 이 구간 실효 디코딩 fps. */
  fpsNum: number;
  /** 현재 적용된 목표 fps(사다리 칸의 fps). */
  targetFps: number;
  /** 표본 시각(ms). */
  tMs: number;
}

export interface AutoState {
  /** 현재 사다리 칸. */
  level: number;
  /** 최근 표본이 나빴는지(최대 BAD_WINDOW 개, 뒤가 최신). */
  recentBad: boolean[];
  /** 연속 좋은 표본 수. */
  goodStreak: number;
  /** 이 시각까지는 아무 판정도 하지 않는다. */
  holdUntilMs: number;
  /** 마지막으로 올린 시각(후회 판정용). 없으면 0. */
  lastUpMs: number;
  /** 올렸다가 곧 내려온 칸 — 이 칸으로는 다시 올라가지 않는다. */
  ceiling: number | null;
  /** 천장 기억이 풀리는 시각. */
  ceilingUntilMs: number;
}

export function initialAutoState(level: number = LADDER_START): AutoState {
  return {
    level: clampLevel(level),
    recentBad: [],
    goodStreak: 0,
    holdUntilMs: 0,
    lastUpMs: 0,
    ceiling: null,
    ceilingUntilMs: 0,
  };
}

function clampLevel(l: number): number {
  return Math.min(Math.max(l, LADDER_TOP), LADDER_BOTTOM);
}

/** 사다리에서 이 조합과 가장 가까운 칸(수동→자동 전환 시 이어서 시작하기 위함). */
export function levelOf(spec: QualitySpec): number {
  const exact = LADDER.findIndex((l) => l.preset === spec.preset && l.fps === spec.fps);
  if (exact >= 0) {
    return exact;
  }
  // 사다리에 없는 조합(수동으로 자유롭게 고른 경우) → 같은 프리셋의 첫 칸, 그것도 없으면 시작점.
  const byPreset = LADDER.findIndex((l) => l.preset === spec.preset);
  return byPreset >= 0 ? byPreset : LADDER_START;
}

export interface AutoDecision {
  state: AutoState;
  /** 바꿔야 하면 새 조합, 아니면 null. */
  next: QualitySpec | null;
  /** 로그·설명용. */
  reason: string;
}

/**
 * 표본 하나를 먹이고 다음 상태를 낸다. **순수 함수** — 같은 입력이면 같은 출력.
 */
export function step(state: AutoState, s: AutoSample): AutoDecision {
  // 쿨다운 중에는 표본을 누적만 하고 판정하지 않는다(방금 바꾼 설정이 자리 잡는 중).
  if (s.tMs < state.holdUntilMs) {
    return { state, next: null, reason: 'cooldown' };
  }

  const bad = s.lossPct >= LOSS_BAD || s.fpsNum < s.targetFps * FPS_BAD_RATIO;
  const good = s.lossPct < LOSS_GOOD && s.fpsNum >= s.targetFps * FPS_GOOD_RATIO;

  const recentBad = [...state.recentBad, bad].slice(-BAD_WINDOW);
  const goodStreak = good ? state.goodStreak + 1 : 0;
  let next: AutoState = { ...state, recentBad, goodStreak };

  // 천장 기억 만료.
  if (next.ceiling !== null && s.tMs >= next.ceilingUntilMs) {
    next = { ...next, ceiling: null, ceilingUntilMs: 0 };
  }

  // ── 내림(빠르게) ──
  const badCount = recentBad.filter(Boolean).length;
  if (badCount >= BAD_NEEDED && next.level < LADDER_BOTTOM) {
    const from = next.level;
    const to = from + 1;
    // 방금 올렸다가 곧 내려간다 = 그 칸은 이 회선에 무리다 → 천장으로 기억한다.
    const regret = next.lastUpMs > 0 && s.tMs - next.lastUpMs <= REGRET_WINDOW_MS;
    next = {
      ...next,
      level: to,
      recentBad: [], // 판정 근거를 소진한다(같은 표본으로 연달아 내리지 않게)
      goodStreak: 0,
      ceiling: regret ? from : next.ceiling,
      ceilingUntilMs: regret ? s.tMs + CEILING_HOLD_MS : next.ceilingUntilMs,
      lastUpMs: 0,
      holdUntilMs: s.tMs + UP_COOLDOWN_MS,
    };
    return {
      state: next,
      next: LADDER[to],
      reason: regret ? 'down-regret' : 'down',
    };
  }

  // ── 올림(느리게) ──
  if (goodStreak >= GOOD_NEEDED && next.level > LADDER_TOP) {
    const to = next.level - 1;
    // 천장으로 기억한 칸이면 올리지 않는다(기억이 풀릴 때까지).
    if (next.ceiling !== null && to <= next.ceiling) {
      return { state: { ...next, goodStreak: 0 }, next: null, reason: 'ceiling' };
    }
    next = {
      ...next,
      level: to,
      recentBad: [],
      goodStreak: 0,
      lastUpMs: s.tMs,
      holdUntilMs: s.tMs + UP_COOLDOWN_MS,
    };
    return { state: next, next: LADDER[to], reason: 'up' };
  }

  return { state: next, next: null, reason: bad ? 'bad' : good ? 'good' : 'neutral' };
}

/** 사다리 칸을 사람이 읽을 문구로(도구막대 표시용). */
export function ladderLabel(level: number): string {
  const l = LADDER[clampLevel(level)];
  const name: Record<PresetId, string> = { low: '낮음', med: '보통', high: '높음' };
  return `${name[l.preset]} · ${l.fps}fps`;
}
